// src/ui/steps/step4-result.jsx — Openingsbod (o.b.v. timesheet) + aannames + onderhandelsignalen + export
import React from "react";
import { FW, FwButton, FwCard, FwInlineBanner, StepHeader, ResultaatSamenvatting } from "../primitives";
import { calc } from "../../lib/calc";
import { aiHelp } from "../../lib/ai";
import { config } from "../../lib/config";
import { niveauIndicatorenFilmAv, sectieLabels } from "../../lib/niveau-indicatoren";
import { opdrachtKompasFilmAv } from "../../lib/opdracht-kompas";
import { exportFilmAv } from "../../lib/docx-export";

export const StepResult = ({ state, setState, back, restart }) => {
  const opdracht = calc.berekenOpdracht(state);
  const huidigeRol = state.rollen?.[0];
  const rolResultaat = opdracht.rolResultaten?.[0]?.result;
  const [copied, setCopied] = React.useState(false);
  const [sharing, setSharing] = React.useState(false);
  const [signalen, setSignalen] = React.useState([]);
  const [loadingSig, setLoadingSig] = React.useState(false);
  const [sigError, setSigError] = React.useState(false);

  React.useEffect(() => {
    if (!huidigeRol || !state.omschrijving) return;
    const ctrl = new AbortController();
    setLoadingSig(true);
    aiHelp.signalOnderhandelpunten(
      state.omschrijving,
      { signal: ctrl.signal },
      {
        rol: huidigeRol.rol,
        niveau: huidigeRol.niveau,
        uren: huidigeRol.uren || {},
        urenDetails: huidigeRol.urenDetails || {},
      }
    ).then(r => {
      if (ctrl.signal.aborted) return;
      setLoadingSig(false);
      if (r?.signalen) setSignalen(r.signalen);
      else setSigError(true);
    });
    return () => ctrl.abort();
  }, []);

  if (!rolResultaat || !huidigeRol) {
    return <div style={{ padding: 40, color: FW.navy }}>Geen berekening mogelijk — ga terug en vul de timesheet in.</div>;
  }

  const isFestival = state.type === "festival";

  const shareLink = () => {
    const json = btoa(unescape(encodeURIComponent(JSON.stringify(state))));
    const url = `${location.origin}${location.pathname}#berekening=${json}`;
    navigator.clipboard.writeText(url);
    setSharing(true);
    setTimeout(() => setSharing(false), 2000);
  };
  const copyEmail = () => {
    const txt = generateEmail(state, opdracht, huidigeRol, rolResultaat);
    navigator.clipboard.writeText(txt);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const downloadWord = () => exportFilmAv({ state, opdracht, huidigeRol, rolResultaat, signalen });

  return (
    <div className="result-page">
      <StepHeader
        tag="Openingsbod"
        title="Jouw minimum starttarief"
        sub="Op basis van de Fair Pay richtlijn 2026 en jouw timesheet."
        onBack={back}
      />

      <ResultaatSamenvatting
        isFestival={isFestival}
        vorm={state.vorm}
        rol={huidigeRol.rol}
        dep={huidigeRol.dep}
        niveau={huidigeRol.niveau}
      />

      <div className="row-2col" style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr", gap: 24, alignItems: "start" }}>
        <div>
          <div style={{ background: FW.white, border: `1.5px solid ${FW.gray100}`, borderRadius: 20, padding: 24 }}>
            <UrenCategorieRegel label="Voorbereiding" uren={rolResultaat.urenVoorbereiding} uurtarief={rolResultaat.uurtarief} details={huidigeRol.urenDetails?.voorbereiding} />
            <UrenCategorieRegel label="Hoofdwerkzaamheden" uren={rolResultaat.urenHoofdwerk} uurtarief={rolResultaat.uurtarief} details={huidigeRol.urenDetails?.hoofdwerk} />
            <UrenCategorieRegel label="Afronding" uren={rolResultaat.urenAfronding} uurtarief={rolResultaat.uurtarief} details={huidigeRol.urenDetails?.afronding} />
            {rolResultaat.urenPrPromotie > 0 && (
              <UrenCategorieRegel label="PR & promotie" uren={rolResultaat.urenPrPromotie} uurtarief={rolResultaat.uurtarief} details={huidigeRol.urenDetails?.pr_promotie} />
            )}
            <div style={{ height: 1, background: FW.gray100, margin: "12px 0" }} />
            <Regel label={`Subtotaal arbeid · ${rolResultaat.urenTotaal} uur`} bedrag={opdracht.subtotaalArbeid} strong />

            <div style={{ height: 1, background: FW.gray100, margin: "12px 0" }} />
            <Regel label="Totaal excl. btw" bedrag={opdracht.totaalExBtw} strong />
            <Regel label="21% btw" bedrag={opdracht.btw} />
            <Regel label="Totaal incl. btw" bedrag={opdracht.totaalInclBtw} strong />
          </div>

          <div style={{ marginTop: 32, fontSize: 20, fontWeight: 700, marginBottom: 6 }}>Aannames</div>
          <div style={{ fontSize: 14, color: FW.navy2, marginBottom: 16 }}>
            Klik op een aanname om de onderbouwing te zien of de waarde aan te passen.
          </div>
          <div style={{ display: "grid", gap: 8 }}>
            {state.omschrijving && (
              <Aanname
                label="Omschrijving"
                waarde={state.omschrijving.length > 80 ? state.omschrijving.slice(0, 80).trim() + "…" : state.omschrijving}
                uitleg="Dit is de tekst die je hebt ingevuld als beschrijving van de opdracht. AI gebruikt deze tekst om rol, niveau en timesheet-uren te suggereren."
                onEdit={() => { back(); back(); back(); }}
                detail={
                  <div style={{ background: FW.white, border: `1px solid ${FW.gray100}`, borderRadius: 10, padding: 12, fontSize: 14, color: FW.navy2, lineHeight: 1.55, whiteSpace: "pre-wrap", marginBottom: 12 }}>
                    {state.omschrijving}
                  </div>
                }
              />
            )}
            <Aanname
              label="Type opdracht"
              waarde={isFestival ? "Filmfestival" : "Film/AV-productie"}
              uitleg={`Voor ${isFestival ? "filmfestivals" : "film/AV-producties"} geldt een eigen Fair Pay richtlijn met ${isFestival ? "12 schalen (1-12, Cao Toneel & Dans)" : "11 zwaarteniveaus (A-K)"}. Dit bepaalt welke tariefkaart we gebruiken.`}
              onEdit={() => restart()}
            />
            <Aanname
              label="Rol / functie"
              waarde={`${huidigeRol.rol} · ${huidigeRol.dep}`}
              uitleg={`Op basis van ${state.omschrijving ? "jouw omschrijving" : "jouw keuze in de rollen-lijst"} is deze rol gekozen uit de Gids Rollen Film/AV${isFestival ? " en Functiehandboek Filmfestivals" : ""}.`}
              onEdit={() => { back(); back(); back(); }}
              detail={<RolOmschrijvingDetail rol={huidigeRol.rol} dep={huidigeRol.dep} isFestival={isFestival} />}
            />
            <Aanname
              label="Zwaarteniveau"
              waarde={`${huidigeRol.niveau}${rolResultaat.tariefRij?.domein ? ` (${rolResultaat.tariefRij.domein})` : (rolResultaat.tariefRij?.rol_voorbeeld ? ` (${rolResultaat.tariefRij.rol_voorbeeld})` : "")}`}
              uitleg={`Het Opdracht Kompas koppelt elke opdracht aan een zwaarteniveau. Niveau ${huidigeRol.niveau} geeft het minimum starttarief van ${calc.fmt(rolResultaat.uurtarief)}/u.`}
              onEdit={() => { back(); back(); }}
              detail={!isFestival ? <ZwaarteNiveauDetail niveau={huidigeRol.niveau} dep={huidigeRol.dep} /> : undefined}
            />
            <Aanname
              label="Opslagfactor"
              waarde={isFestival ? "69,26% (digiPACCT)" : "74,45% (Ketentafel Film/AV)"}
              uitleg={isFestival
                ? "Voor filmfestivals geldt de digiPACCT-methode: bedrijfskosten (15%), niet-declarabele uren (17%), vakantietoeslag (8%), ziekte/arbeidsongeschiktheid (14,5%), ZVW (5,32%) en pensioen (9,44%). Samen: 69,26%."
                : "De opslagfactor dekt niet-declarabele kosten (15%), niet-declarabele tijd (25%), vakantietoeslag (8%), eindejaarsuitkering (3,45%), ZVW (6%), arbeidsongeschiktheid (7%) en pensioen (10%). Samen: 74,45%."}
              detail={<OpslagTabel isFestival={isFestival} />}
            />
            <Aanname
              label="Voorbereiding"
              waarde={`${rolResultaat.urenVoorbereiding} uur`}
              uitleg="Wat je doet vóór het hoofdwerk kan beginnen — research, scripts lezen, locatiebezoek, stijlbespreking, etc. Telt als betaalde tijd."
              onEdit={() => back()}
              detail={<UrenDetails details={huidigeRol.urenDetails?.voorbereiding} />}
            />
            <Aanname
              label="Hoofdwerkzaamheden"
              waarde={`${rolResultaat.urenHoofdwerk} uur`}
              uitleg="De kernactiviteiten van je opdracht — het directe werk waar je opdrachtgever je voor inhuurt."
              onEdit={() => back()}
              detail={<UrenDetails details={huidigeRol.urenDetails?.hoofdwerk} />}
            />
            <Aanname
              label="Afronding"
              waarde={`${rolResultaat.urenAfronding} uur`}
              uitleg="Wat je ná het hoofdwerk doet om de opdracht echt af te ronden — overdracht, exports, archivering, opruimen."
              onEdit={() => back()}
              detail={<UrenDetails details={huidigeRol.urenDetails?.afronding} />}
            />
            {rolResultaat.urenPrPromotie > 0 && (
              <Aanname
                label="PR & promotie"
                waarde={`${rolResultaat.urenPrPromotie} uur`}
                uitleg="Promotionele werkzaamheden die onderdeel zijn van de opdracht — interviews, premières, persdagen."
                onEdit={() => back()}
                detail={<UrenDetails details={huidigeRol.urenDetails?.pr_promotie} />}
              />
            )}
          </div>
        </div>

        <div className="aside-card" style={{ display: "grid", gap: 16, position: "sticky", top: 20 }}>
          <FwCard padded={false} style={{ padding: 22, background: FW.cream }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: FW.navy, marginBottom: 10 }}>Goed om te benoemen</div>
            <ul style={{ fontSize: 14, color: FW.navy2, margin: 0, paddingLeft: 16, lineHeight: 1.55, display: "grid", gap: 8 }}>
              <li>Dit is een <b>minimum</b> starttarief volgens Fair Pay. Meer ervaring, complexiteit of onregelmatige uren zijn <b>onderhandelruimte</b>.</li>
              <li>Voor eventuele reiskosten, apparatuur, materialen of inhuur derden kun je <b>separaat afspraken</b> maken. Reis-, materiaal- en inhuurkosten mag je apart factureren bovenop het uurtarief.</li>
              <li>Het tarief bevat al een <b>opslag van {isFestival ? "69,26%" : "74,45%"}</b> voor zzp-lasten, offerte-tijd en niet-declarabele kosten. Het zzp-starttarief is inclusief een opslagfactor van {isFestival ? "69,26%" : "74,45%"} voor onder meer niet-declarabele tijd, niet-declarabele kosten, vakantie, pensioen en ZVW.</li>
            </ul>
          </FwCard>

          {loadingSig && (
            <div style={{ padding: 16, background: FW.beige2, borderRadius: 14, fontSize: 14, color: FW.navy, display: "inline-flex", alignItems: "center", gap: 10 }}>
              <i className="ri-sparkling-2-line" />
              Aandachtspunten voor je onderhandeling worden geladen…
            </div>
          )}
          {!loadingSig && sigError && (
            <FwInlineBanner type="warning" onDismiss={() => setSigError(false)}>
              <div style={{ fontWeight: 700 }}>Onderhandelpunten konden niet geladen worden.</div>
            </FwInlineBanner>
          )}
          {!loadingSig && (
            <FwCard padded={false} style={{ padding: 22, background: FW.cream, border: `1.5px solid ${FW.yellow}` }}>
              <h3 style={{ margin: "0 0 6px", fontSize: 14, fontWeight: 700, color: FW.navy, display: "inline-flex", alignItems: "center", gap: 6 }}>
                <i className="ri-chat-3-line" /> Aandachtspunten voor je onderhandeling
              </h3>
              <p style={{ fontSize: 13, color: FW.navy2, margin: "0 0 8px", lineHeight: 1.45 }}>
                Dit openingsbod is het minimum starttarief volgens Fair Pay. Denk bij je onderhandeling bijvoorbeeld aan:
              </p>
              <ul style={{ margin: "0 0 12px", paddingLeft: 16, display: "grid", gap: 4 }}>
                <li style={{ fontSize: 13, color: FW.navy2, lineHeight: 1.45 }}>De complexiteit van de opdracht.</li>
                <li style={{ fontSize: 13, color: FW.navy2, lineHeight: 1.45 }}>Jouw ervaring, reputatie of specialisme.</li>
                <li style={{ fontSize: 13, color: FW.navy2, lineHeight: 1.45 }}>De specifieke aard van de productie.</li>
                <li style={{ fontSize: 13, color: FW.navy2, lineHeight: 1.45 }}>Ongebruikelijke werktijden en -locaties.</li>
                <li style={{ fontSize: 13, color: FW.navy2, lineHeight: 1.45 }}>De looptijd van een opdracht.</li>
                <li style={{ fontSize: 13, color: FW.navy2, lineHeight: 1.45 }}>Eventueel declarabel over- of meerwerk.</li>
                <li style={{ fontSize: 13, color: FW.navy2, lineHeight: 1.45 }}>Beschikbaarheid voor inzet binnen de productie gedurende een langere periode.</li>
              </ul>
              {signalen.length > 0 && (
                <>
                  <p style={{ fontSize: 13, fontWeight: 700, color: FW.navy, margin: "0 0 6px", lineHeight: 1.45 }}>
                    Specifiek uit jouw omschrijving:
                  </p>
                  <ul style={{ margin: "0 0 12px", paddingLeft: 16, display: "grid", gap: 6 }}>
                    {signalen.map((s, i) => (
                      <li key={i} style={{ fontSize: 13, color: FW.navy2, lineHeight: 1.45 }}>
                        <strong style={{ color: FW.navy }}>{s.factor}</strong> — {s.toelichting}
                        {s.leidraad_verwijzing && (
                          <div style={{ fontSize: 12, color: FW.gray500, marginTop: 2 }}>
                            Zie ook: Leidraad veilig werken, onderdeel "{s.leidraad_verwijzing}"
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {!isFestival && (
                <p style={{ fontSize: 12, color: FW.navy2, margin: 0, lineHeight: 1.45 }}>
                  Zie ook de <strong>Leidraad veilig en gezond werken AV-sector</strong> — bijvoorbeeld voor afspraken over meerwerk bij veranderde opdracht, meeruren vanaf bepaalde omvang en tijd-voor-tijd regelingen.
                </p>
              )}
            </FwCard>
          )}
        </div>
      </div>

      <FwCard tint="navy" padded={false} className="summary-card" style={{ padding: "40px 44px", marginTop: 32 }}>
        <div className="summary-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 32, alignItems: "end" }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: FW.yellow, opacity: .9 }}>Uurtarief</div>
            <div className="summary-value" style={{ fontSize: 48, fontWeight: 700, color: "#fff", lineHeight: 1, marginTop: 8, fontVariantNumeric: "tabular-nums" }}>
              {calc.fmt(rolResultaat.uurtarief)}
            </div>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,.65)", marginTop: 6 }}>Incl. {isFestival ? "69,26%" : "74,45%"} opslag</div>
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: FW.yellow, opacity: .9 }}>Totaal uren</div>
            <div className="summary-value" style={{ fontSize: 48, fontWeight: 700, color: "#fff", lineHeight: 1, marginTop: 8, fontVariantNumeric: "tabular-nums" }}>
              {rolResultaat.urenTotaal}
            </div>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,.65)", marginTop: 6 }}>
              {rolResultaat.urenHoofdwerk} hoofd · {rolResultaat.urenVoorbereiding} voor · {rolResultaat.urenAfronding} na{rolResultaat.urenPrPromotie ? ` · ${rolResultaat.urenPrPromotie} pr` : ""}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: FW.yellow, opacity: .9 }}>Openingsbod</div>
            <div className="summary-value" style={{ fontSize: 48, fontWeight: 700, color: "#fff", lineHeight: 1, marginTop: 8, fontVariantNumeric: "tabular-nums" }}>
              {calc.fmtInt(rolResultaat.openingsbod)}
            </div>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,.65)", marginTop: 6 }}>
              Excl. btw · incl: {calc.fmtInt(opdracht.totaalInclBtw)}
            </div>
          </div>
        </div>
      </FwCard>

      <FwCard padded={false} style={{ marginTop: 24, padding: 22, background: FW.mint }} className="hide-print">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
          <div style={{ minWidth: 0, flex: "1 1 260px" }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: FW.navy, marginBottom: 4, display: "inline-flex", alignItems: "center", gap: 6 }}>
              <i className="ri-send-plane-line" /> Deel je openingsbod
            </div>
            <div style={{ fontSize: 14, color: FW.navy2, lineHeight: 1.4 }}>
              Deel via een link, kopieer een e-mailtekst, of download als onderbouwingsdocument.
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <FwButton variant="navy" icon="ri-link" arrow={false} onClick={shareLink}>
              {sharing ? "Link gekopieerd ✓" : "Kopieer deelbare link"}
            </FwButton>
            <FwButton variant="secondary" icon="ri-mail-line" arrow={false} onClick={copyEmail}>
              {copied ? "E-mailtekst gekopieerd ✓" : "Kopieer e-mailtekst"}
            </FwButton>
            <FwButton variant="secondary" icon="ri-file-word-2-line" arrow={false} onClick={downloadWord}>
              Download als Word
            </FwButton>
          </div>
        </div>
      </FwCard>

      <div style={{ marginTop: 32, display: "flex", justifyContent: "space-between" }} className="hide-print step-nav">
        <FwButton variant="ghost" arrow={false} onClick={back}>← Pas timesheet aan</FwButton>
        <FwButton variant="secondary" arrow={false} onClick={restart}>Nieuwe berekening</FwButton>
      </div>
    </div>
  );
};

const UrenCategorieRegel = ({ label, uren, uurtarief, details }) => {
  if (!uren || uren <= 0) return null;
  const details_ingevuld = Array.isArray(details) ? details.filter(d => d.activiteit && (Number(d.uren) || 0) > 0) : [];
  return (
    <div style={{ padding: "4px 0" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", padding: "8px 0", fontSize: 15, color: FW.navy }}>
        <span>{label} · {uren} uur × {calc.fmt(uurtarief)}</span>
        <span style={{ fontWeight: 700 }}>{calc.fmt(uren * uurtarief)}</span>
      </div>
      {details_ingevuld.length > 0 && (
        <div style={{ paddingLeft: 14, fontSize: 13, color: FW.gray500, display: "grid", gap: 2, marginTop: -4, marginBottom: 4 }}>
          {details_ingevuld.map(d => (
            <div key={d.id}>— {d.activiteit} ({d.uren} uur)</div>
          ))}
        </div>
      )}
    </div>
  );
};

const UrenDetails = ({ details }) => {
  const rijen = Array.isArray(details) ? details.filter(d => d.activiteit && (Number(d.uren) || 0) > 0) : [];
  if (!rijen.length) return null;
  return (
    <div style={{ background: FW.white, border: `1px solid ${FW.gray100}`, borderRadius: 10, padding: 12, fontSize: 13, marginBottom: 12 }}>
      {rijen.map(d => (
        <div key={d.id} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}>
          <span style={{ color: FW.navy2 }}>{d.activiteit}</span>
          <span style={{ fontWeight: 700, color: FW.navy }}>{d.uren} uur</span>
        </div>
      ))}
    </div>
  );
};

export const Regel = ({ label, bedrag, strong }) => (
  <div style={{
    display: "flex", justifyContent: "space-between", alignItems: "baseline",
    padding: "8px 0", fontSize: strong ? 17 : 15, fontWeight: strong ? 700 : 400, color: FW.navy,
  }}>
    <span>{label}</span>
    <span style={{ fontWeight: 700 }}>{calc.fmt(bedrag)}</span>
  </div>
);

export const Aanname = ({ label, waarde, uitleg, detail, onEdit }) => {
  const [open, setOpen] = React.useState(false);

  return (
    <div style={{ background: FW.white, border: `1.5px solid ${FW.gray100}`, borderRadius: 14, overflow: "hidden" }}>
      <div onClick={() => setOpen(!open)} style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 10,
        padding: "14px 18px", cursor: "pointer",
      }}>
        <div className="aanname-row" style={{ display: "flex", alignItems: "baseline", gap: 12, flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: FW.gray500, textTransform: "uppercase", letterSpacing: 0.5, minWidth: 160 }}>{label}</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: FW.navy, wordBreak: "break-word" }}>{waarde}</div>
        </div>
        <i className="ri-arrow-down-s-line" style={{
          fontSize: 22, color: FW.gray500,
          transform: open ? "rotate(180deg)" : "rotate(0)", transition: "transform .15s",
        }} />
      </div>
      {open && (
        <div style={{ padding: "0 18px 18px", background: FW.cream }}>
          <div style={{ fontSize: 14, lineHeight: 1.5, color: FW.navy2, padding: "6px 0 12px" }}>{uitleg}</div>
          {detail}
          {onEdit && (
            <button onClick={onEdit} style={{
              background: "transparent", color: FW.navy, border: `1.5px solid ${FW.navy}`,
              borderRadius: 999, padding: "6px 14px", fontWeight: 700, fontSize: 13,
              cursor: "pointer", fontFamily: "var(--font-sans)",
            }}>
              <i className="ri-pencil-line" style={{ marginRight: 6 }} /> Aanpassen
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// Detail-render voor de Rol-aanname: toont de functie-omschrijving uit de Gids
// Rollen (Film/AV) of het Functiehandboek Filmfestivals.
export const RolOmschrijvingDetail = ({ rol, dep, isFestival }) => {
  const rollen = isFestival ? config.rollen_festival : config.rollen_film_av;
  const r = rollen.find(x => x.dep === dep && x.rol === rol);
  if (!r?.omschrijving) return null;
  return (
    <div style={{ background: FW.white, border: `1px solid ${FW.gray100}`, borderRadius: 10, padding: 12, fontSize: 14, color: FW.navy2, lineHeight: 1.55, marginBottom: 12, whiteSpace: "pre-line" }}>
      {r.omschrijving}
    </div>
  );
};

// Detail-render voor de Zwaarteniveau-aanname (alleen Film & AV): Opdracht Kompas
// per departement × niveau, plus de USB-punten en sectie-indicatoren.
export const ZwaarteNiveauDetail = ({ niveau, dep }) => {
  const kompasTekst = opdrachtKompasFilmAv[niveau]?.[dep];
  const indicatoren = niveauIndicatorenFilmAv[niveau];
  if (!kompasTekst && !indicatoren) return null;
  const refMatch = kompasTekst?.match(/\n+\s*Referentie\/interview:\s*(.+)$/is);
  const hoofdtekst = refMatch ? kompasTekst.slice(0, refMatch.index).trim() : kompasTekst?.trim();
  const referentie = refMatch ? refMatch[1].trim() : null;
  return (
    <div style={{ display: "grid", gap: 10, marginBottom: 12 }}>
      {kompasTekst && (
        <div style={{ background: FW.white, border: `1px solid ${FW.gray100}`, borderRadius: 10, padding: "12px 14px" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: FW.navy, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8, display: "inline-flex", alignItems: "center", gap: 6 }}>
            <i className="ri-compass-3-line" style={{ fontSize: 14 }} />
            Opdracht Kompas — {dep} · niveau {niveau}
          </div>
          <div style={{ fontSize: 13, lineHeight: 1.55, color: FW.navy2, whiteSpace: "pre-line" }}>
            {hoofdtekst}
          </div>
          {referentie && (
            <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${FW.gray100}`, fontSize: 12, color: FW.gray500, fontStyle: "italic" }}>
              Referentie/interview: {referentie}
            </div>
          )}
        </div>
      )}
      {indicatoren && (
        <div style={{ background: FW.white, border: `1px solid ${FW.gray100}`, borderRadius: 10, padding: "12px 14px" }}>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 8, gap: 12, flexWrap: "wrap" }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: FW.navy, textTransform: "uppercase", letterSpacing: 0.5 }}>
              Niveau-indicatoren — niveau {niveau}
            </div>
            <div style={{ fontSize: 12, color: FW.gray500 }}>USB-punten {indicatoren.usb}</div>
          </div>
          <div style={{ fontSize: 13, lineHeight: 1.55, color: FW.navy2 }}>
            {Object.entries(indicatoren.secties).map(([key, tekst]) => (
              <div key={key} style={{ marginBottom: 8 }}>
                <div style={{ fontWeight: 700, color: FW.navy, marginBottom: 2 }}>{sectieLabels[key]}</div>
                <div>{tekst}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const OpslagTabel = ({ isFestival }) => {
  const componenten = isFestival ? config.opslag_festival : config.opslag_film_av;
  const totaalLabel = isFestival ? "69,26%" : "74,45%";
  return (
    <div style={{ background: FW.white, border: `1px solid ${FW.gray100}`, borderRadius: 10, padding: 12, fontSize: 13, marginBottom: 12 }}>
      {componenten.map((c, i) => (
        <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderBottom: i < componenten.length - 1 ? `1px dashed ${FW.gray100}` : "none" }}>
          <span>{c.label}</span><span style={{ fontWeight: 700 }}>{(c.pct*100).toFixed(2).replace(".00","").replace(".",",")}%</span>
        </div>
      ))}
      <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0 2px", marginTop: 4, borderTop: `1.5px solid ${FW.navy}`, fontWeight: 700 }}>
        <span>Totaal</span><span>{totaalLabel}</span>
      </div>
    </div>
  );
};

function generateEmail(state, opdracht, rol, rolResultaat) {
  const isFestival = state.type === "festival";
  const urenDetails = rol.urenDetails || {};
  const detailRegels = (cat, label) => {
    const rijen = Array.isArray(urenDetails[cat]) ? urenDetails[cat].filter(d => d.activiteit && (Number(d.uren) || 0) > 0) : [];
    if (!rijen.length) return "";
    const lijst = rijen.map(r => `    - ${r.activiteit}: ${r.uren} uur`).join("\n");
    const totaal = rijen.reduce((s, r) => s + (Number(r.uren) || 0), 0);
    return `\n  ${label} (${totaal} uur):\n${lijst}`;
  };

  const signalen = (rol.onderhandelsignalen || []).map(s => `  - ${s.factor}: ${s.toelichting}`).join("\n");

  return `Beste [opdrachtgever],

Hierbij een openingsbod voor mijn inzet als ${rol.rol} (${rol.dep}) op jouw ${isFestival ? "filmfestival" : "filmproductie"}.

Op basis van de Fair Pay richtlijn 2026 (zwaarteniveau ${rol.niveau}${!isFestival ? " volgens Opdracht Kompas Film/AV-productie" : ""}):

• Uurtarief: ${calc.fmt(rolResultaat.uurtarief)} (incl. ${isFestival ? "69,26%" : "74,45%"} opslag)
• Totale tijdsinvestering: ${rolResultaat.urenTotaal} uur
• Openingsbod: ${calc.fmtInt(rolResultaat.openingsbod)} excl. btw

Tijdsinvestering per categorie:${detailRegels("voorbereiding", "Voorbereiding")}${detailRegels("hoofdwerk", "Hoofdwerkzaamheden")}${detailRegels("afronding", "Afronding")}${detailRegels("pr_promotie", "PR & promotie")}
${signalen ? `\nAandachtspunten voor onze onderhandeling:\n${signalen}\n` : ""}
Dit is een minimum starttarief conform de Fair Pay richtlijn van de sector. Ik ga graag in gesprek over scope, aanvullende werkzaamheden of specifieke omstandigheden van jouw productie.

Met vriendelijke groet,
[jouw naam]`;
}
